import { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import PersonAddIcon from '@mui/icons-material/PersonAdd'

import { useAttachPatientDialog } from '../model/useAttachPatientDialog'

type AttachPatientDialogProps = {
  open: boolean
  onClose: () => void
}

export function AttachPatientDialog({ open, onClose }: AttachPatientDialogProps) {
  useLingui()
  const [email, setEmail] = useState('')

  const { search, attach, searchResults, isFetching, isAttaching, searchTriggered } = useAttachPatientDialog(onClose)

  const handleSearch = () => {
    search(email)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon color="primary" />
          <Trans>Attach Patient</Trans>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label={t`Email`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} disabled={!email || isFetching}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isFetching && (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}

          {!isFetching && searchTriggered && searchResults.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography color="textSecondary">
                <Trans>No patients found</Trans>
              </Typography>
            </Box>
          )}

          {!isFetching && searchResults.length > 0 && (
            <List>
              {searchResults.map((patient) => (
                <ListItem key={patient._id} divider>
                  <ListItemText primary={`${patient.userInfo.name} ${patient.userInfo.surname}`} secondary={patient.email} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" color="primary" onClick={() => attach(patient._id)} disabled={isAttaching}>
                      {isAttaching ? <CircularProgress size={24} /> : <AddIcon />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          <Trans>Cancel</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
